import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { newsCardsApi } from '../lib/api';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const NewsCardList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: newsCards = [], isLoading } = useQuery({
    queryKey: ['news-cards'],
    queryFn: () => newsCardsApi.getAll().then((res: any) => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => newsCardsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-cards'] });
      toast.success(t('newsCardDeleted'));
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const filteredNewsCards = newsCards.filter((card: any) =>
    card.title_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.title_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.summary_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.summary_ar?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">
          {i18n.language === 'ar' ? 'إدارة كروت الأخبار' : 'News Cards Management'}
        </h1>
        <Button onClick={() => navigate('/news-cards/new')}>
          <Plus className="h-4 w-4 mr-2" />
          {i18n.language === 'ar' ? 'إضافة كرت أخبار' : 'Add News Card'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{i18n.language === 'ar' ? 'قائمة كروت الأخبار' : 'News Cards List'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder={i18n.language === 'ar' ? 'البحث في كروت الأخبار...' : 'Search news cards...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className={`overflow-x-auto ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
            <Table className={i18n.language === 'ar' ? '[&_th]:text-right [&_td]:text-right' : '[&_th]:text-left [&_td]:text-left'}>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.language === 'ar' ? 'العنوان' : 'Title'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'الملخص' : 'Summary'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'الخبر المرتبط' : 'Related News'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'تاريخ الإنشاء' : 'Created'}</TableHead>
                  <TableHead>{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNewsCards.map((card: any) => (
                  <TableRow key={card.id}>
                    <TableCell>
                      <div className="font-medium">
                        <div className="mb-1">
                          {i18n.language === 'ar' ? (card.title_ar || card.title_en) : (card.title_en || card.title_ar)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {i18n.language === 'ar' ? 'EN:' : 'AR:'} {i18n.language === 'ar' ? card.title_en : card.title_ar}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        <div className="mb-1">
                          {i18n.language === 'ar' ? (card.summary_ar || card.summary_en) : (card.summary_en || card.summary_ar)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {i18n.language === 'ar' ? 'EN:' : 'AR:'} {i18n.language === 'ar' ? card.summary_en : card.summary_ar}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {card.news ? (
                        <div>
                          <Badge variant="secondary" className="mb-1">
                            {i18n.language === 'ar' ? (card.news.title_ar || card.news.title) : (card.news.title || card.news.title_ar)}
                          </Badge>
                          <div className="text-xs text-gray-500">
                            ID: {card.news_id}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">
                          {i18n.language === 'ar' ? 'لا يوجد خبر مرتبط' : 'No related news'}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500">
                        {new Date(card.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2 rtl:space-x-reverse">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/news-cards/${card.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/news-cards/${card.id}/edit`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {i18n.language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {i18n.language === 'ar' 
                                  ? 'هل أنت متأكد من حذف هذا الكرت؟ لا يمكن التراجع عن هذا الإجراء.'
                                  : 'Are you sure you want to delete this news card? This action cannot be undone.'
                                }
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(card.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {i18n.language === 'ar' ? 'حذف' : 'Delete'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredNewsCards.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {i18n.language === 'ar' ? 'لا توجد كروت أخبار' : 'No news cards found'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsCardList;
